import { useState } from 'react';
import { Copy, Check, Download, Send, ShoppingBag } from 'lucide-react';
import Sheet from './Sheet';
import { track } from '../lib/track';
import { isIOS } from '../lib/device';
import cardRender from '@/assets/hexlink-card.png';

export const SHOP_URL = 'https://nordic3d.us/hexlink.html';

export function shopHref(placement, theme) {
    const p = new URLSearchParams({
        utm_source: 'hexlink',
        utm_medium: 'card',
        utm_campaign: placement,
        utm_content: theme || 'default',
    });
    return `${SHOP_URL}?${p}`;
}

export function QRSheet({ open, onClose, uuid, cardUrl, isOwner, portalTarget }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(cardUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            // clipboard blocked; the link is still visible to copy by hand
        }
    };
    return (
        <Sheet open={open} onClose={onClose} title="Scan to open this card" subtitle="Point any phone camera at the code." portalTarget={portalTarget}>
            <img className="hx-qr" src={`/api/card/${uuid}/qrcode?format=svg`} alt="QR code for this card" width="260" height="260" />
            <div style={{ display: 'grid', gap: 10 }}>
                <button type="button" className="hx-btn hx-btn-ghost hx-btn-block" onClick={copy}>
                    {copied ? <Check /> : <Copy />}
                    {copied ? 'Link copied' : 'Copy card link'}
                </button>
                {isOwner && (
                    <a className="hx-btn hx-btn-ghost hx-btn-block" href={`/api/card/${uuid}/qrcode?size=1024`} download="hexlink-qr.png">
                        <Download />
                        Save QR image
                    </a>
                )}
            </div>
        </Sheet>
    );
}

export function SaveHintSheet({ open, onClose, vcardUrl, portalTarget }) {
    return (
        <Sheet open={open} onClose={onClose} title="Almost saved" subtitle="Your phone downloaded the contact file." portalTarget={portalTarget}>
            {(close) => (
                <>
                    <ol className="hx-steps">
                        {isIOS ? (
                            <>
                                <li><span>Tap the contact preview that opened</span></li>
                                <li><span>Tap <strong>Create New Contact</strong></span></li>
                                <li><span>Tap <strong>Done</strong></span></li>
                            </>
                        ) : (
                            <>
                                <li><span>Open the download notification, or your Downloads</span></li>
                                <li><span>Tap the .vcf file</span></li>
                                <li><span>Choose <strong>Contacts</strong> and tap <strong>Save</strong></span></li>
                            </>
                        )}
                    </ol>
                    <div style={{ display: 'grid', gap: 10 }}>
                        <a className="hx-btn hx-btn-ghost hx-btn-block" href={vcardUrl}>
                            <Download />
                            Download again
                        </a>
                        <button type="button" className="hx-btn hx-btn-primary hx-btn-block" onClick={close}>
                            Got it
                        </button>
                    </div>
                </>
            )}
        </Sheet>
    );
}

export function PromoSheet({ open, onClose, uuid, theme, portalTarget }) {
    return (
        <Sheet open={open} onClose={onClose} title="That was a HexLink" subtitle="One tap. Contact saved. No app needed." portalTarget={portalTarget}>
            {(close) => (
                <>
                    <img className="hx-promo-img" src={cardRender} alt="HexLink NFC business card" width="726" height="420" loading="lazy" />
                    <p className="hx-promo-text">
                        Get your own tap-to-share card with a profile like this one. Pick a theme, update it anytime.
                    </p>
                    <div style={{ display: 'grid', gap: 10 }}>
                        <a
                            className="hx-btn hx-btn-primary hx-btn-block"
                            href={shopHref('post_save', theme)}
                            onClick={() => track(uuid, 'promo_click', { label: 'post_save' })}
                        >
                            <ShoppingBag />
                            Get my HexLink
                        </a>
                        <button type="button" className="hx-btn hx-btn-ghost hx-btn-block" onClick={close}>
                            Not now
                        </button>
                    </div>
                </>
            )}
        </Sheet>
    );
}

const EMPTY = { name: '', phone: '', email: '', company: '', note: '', website: '' };

export function ExchangeSheet({ open, onClose, uuid, firstName, preview, portalTarget }) {
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState('idle'); // idle | sending | done | error
    const [error, setError] = useState('');

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return setError('Add your name.');
        if (!form.phone.trim() && !form.email.trim()) return setError('Add a phone number or email.');
        setError('');
        if (preview) {
            setStatus('done');
            return undefined;
        }
        setStatus('sending');
        try {
            const res = await fetch(`/api/card/${uuid}/exchange`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) throw new Error(data.message || 'Could not send. Try again.');
            setStatus('done');
        } catch (err) {
            setStatus('error');
            setError(err.message);
        }
        return undefined;
    };

    const handleClose = () => {
        onClose();
        if (status === 'done') {
            setForm(EMPTY);
            setStatus('idle');
        }
    };

    return (
        <Sheet
            open={open}
            onClose={handleClose}
            title={status === 'done' ? 'Sent!' : `Share your info with ${firstName}`}
            subtitle={status === 'done' ? `${firstName} has your details now.` : `${firstName} gets your details by email. Nothing is shared publicly.`}
            portalTarget={portalTarget}
        >
            {(close) =>
                status === 'done' ? (
                    <button type="button" className="hx-btn hx-btn-primary hx-btn-block" onClick={close}>
                        <Check />
                        Done
                    </button>
                ) : (
                    <form className="hx-form" onSubmit={submit} noValidate>
                        <div className="hx-field">
                            <label htmlFor="hx-x-name">Name</label>
                            <input id="hx-x-name" autoComplete="name" value={form.name} onChange={set('name')} maxLength={100} required />
                        </div>
                        <div className="hx-field">
                            <label htmlFor="hx-x-phone">Phone</label>
                            <input id="hx-x-phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} maxLength={40} />
                        </div>
                        <div className="hx-field">
                            <label htmlFor="hx-x-email">Email</label>
                            <input id="hx-x-email" type="email" inputMode="email" autoComplete="email" value={form.email} onChange={set('email')} maxLength={120} />
                        </div>
                        <div className="hx-field">
                            <label htmlFor="hx-x-company">Company (optional)</label>
                            <input id="hx-x-company" autoComplete="organization" value={form.company} onChange={set('company')} maxLength={100} />
                        </div>
                        <div className="hx-field">
                            <label htmlFor="hx-x-note">Note (optional)</label>
                            <textarea id="hx-x-note" value={form.note} onChange={set('note')} maxLength={500} placeholder="Where you met, what you talked about…" />
                        </div>
                        {/* Honeypot: hidden from people, bots fill it in */}
                        <input className="hx-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set('website')} name="website" />
                        {error && (
                            <p className="hx-error" role="alert">
                                {error}
                            </p>
                        )}
                        <button type="submit" className="hx-btn hx-btn-primary hx-btn-block" disabled={status === 'sending'}>
                            <Send />
                            {status === 'sending' ? 'Sending…' : `Send to ${firstName}`}
                        </button>
                        <p className="hx-fineprint">
                            Only {firstName} sees this.
                        </p>
                    </form>
                )
            }
        </Sheet>
    );
}
